import {
  Alert,
  Button,
  Form,
  Input,
  Result,
  Skeleton,
  Typography
} from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  activateLicencePublic,
  formatLicenceDate,
  getPublicLicenceStatus,
  keyShapeRule,
  licenceErrorMessage,
  type PublicLicenceStatus
} from '../../../service/licence.service';
import { messageFor } from '../../../service/mfa.service';

/**
 * The trial-ended screen.
 *
 * Reached whenever the API refuses a call with 402 (`service/api.tsx` sends every such refusal
 * here), or by its address. It is the one place a key can be applied while nobody can sign in,
 * so it needs no session. It reads its state from the public status route rather than from the
 * refusal that sent it here, because a full page load lies between the two.
 */

type Notice = {
  type: 'warning' | 'error';
  title: string;
  body: string;
};

const noticeFor = (status: PublicLicenceStatus): Notice => {
  switch (status.state) {
    case 'clock_rolled_back':
      return {
        type: 'error',
        title: 'The appliance\'s clock is behind',
        body:
          'Its date is earlier than a time the appliance has already recorded, so the licence '
          + 'cannot be checked. Correct the appliance\'s date and time. Sign-in returns by itself '
          + 'once the clock is right.'
      };
    case 'missing':
      return {
        type: 'error',
        title: 'This appliance has no licence record',
        body: 'Contact SeeWard. Nothing on the appliance has been deleted.'
      };
    default:
      return {
        type: 'warning',
        title: status.was_licensed ? 'Your SeeWard licence has ended' : 'Your SeeWard trial has ended',
        body:
          'Nothing on the appliance has been deleted. Send SeeWard the appliance ID below, then '
          + 'paste the licence key you receive into the box underneath. Everything is back as '
          + 'soon as the key is accepted.'
      };
  }
};

const LicenceFeature = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<PublicLicenceStatus | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const { data } = await getPublicLicenceStatus();
      setStatus(data);
    } catch (err: any) {
      setLoadError(messageFor(err, {}, 'Cannot read the appliance\'s licence.'));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toSignIn = (notice?: string) =>
    navigate('/login', notice ? { 
      replace: true, 
      state: { notice } 
    } : { 
      replace: true 
    });

  const apply = async ({ key }: { key: string }) => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const { data } = await activateLicencePublic(key);
      const holder = data.licensed_to ? ` for ${data.licensed_to}` : '';
      toSignIn(
        `Licence applied${holder}, valid until ${formatLicenceDate(data.expires_at)}. Sign in to continue.`
      );
    } catch (err: any) {
      setSubmitError(licenceErrorMessage(err));
      // Applied meanwhile from somewhere else, or the clock was corrected: say so on the screen.
      if (err?.response?.data?.detail?.code === 'not_blocked') load();
    } finally {
      setSubmitting(false);
    }
  };

  if (loadError) {
    return (
      <Alert
        type="error"
        showIcon
        message={loadError}
        action={
          <Button size="small" onClick={load}>
            Try again
          </Button>
        }
      />
    );
  }

  if (!status) return <Skeleton active paragraph={{ rows: 4 }} />;

  if (!status.enabled || !status.blocked) {
    return (
      <Result
        status={status.enabled ? 'success' : 'info'}
        title={status.enabled ? 'This appliance is ready' : 'Nothing to do here'}
        subTitle={
          status.enabled
            ? 'Its trial or licence is current. A Superadmin can see the details under Settings, Licence.'
            : 'Licensing does not apply to this deployment.'
        }
        extra={
          <Button type="primary" onClick={() => toSignIn()}>
            Go to sign in
          </Button>
        }
      />
    );
  }

  const notice = noticeFor(status);
  // Activation refuses both of the other blocking states, so offering the box would only
  // collect a key the server is bound to turn down.
  const canApply = status.state === 'expired';

  return (
    <div>
      <Alert
        type={notice.type}
        showIcon
        className="mb-6"
        message={notice.title}
        description={<span className="text-[13px]">{notice.body}</span>}
      />

      {status.appliance_id && (
        <div className="mb-6">
          <Typography.Text type="secondary" className="block text-[13px] mb-1">
            Appliance ID
          </Typography.Text>
          <Typography.Text code copyable className="text-[15px]">
            {status.appliance_id}
          </Typography.Text>
        </div>
      )}

      {canApply ? (
        <Form layout="vertical" onFinish={apply} requiredMark={false}>
          {submitError && (
            <Alert
              type="error"
              showIcon
              className="mb-4"
              message={submitError}
              closable
              onClose={() => setSubmitError(null)}
            />
          )}
          <Form.Item
            name="key"
            label="Licence key"
            rules={[
              {
                required: true,
                message: 'Paste the licence key SeeWard sent you'
              }, keyShapeRule
            ]}
          >
            <Input.TextArea
              autoSize={{ minRows: 3, maxRows: 8 }}
              placeholder="SW1...."
              spellCheck={false}
              className="font-mono"
            />
          </Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            block
            loading={submitting}
          >
            Apply key
          </Button>
        </Form>
      ) : (
        <Button size="large" block onClick={load}>
          Check again
        </Button>
      )}

      <div className="flex justify-center mt-5">
        <Button type="link" onClick={() => toSignIn()}>
          Back to sign in
        </Button>
      </div>
    </div>
  );
};

export default LicenceFeature;
